// The 100-step AI Mastery Path that powers the rotating "learning launch"
// strip on the home page. Five tiers, twenty skills each. Each skill is
// a click target — tapping one seeds the playbook search with the skill
// name so the runner can route the user into the most relevant playbook.
//
// Tiers progress from "stop being a consumer" (Awareness) to "build the
// future" (Architecture). The order inside each tier is intentional and
// shouldn't be reshuffled — it reflects the real learning curve.

export type MasteryTier = "Awareness" | "Application" | "Leverage" | "Systems" | "Architecture";

export type MasterySkill = {
  /** 1-based position in the full 100-step path. Used to render "#42". */
  num: number;
  tier: MasteryTier;
  label: string;
};

/** Visual + narrative metadata for each tier (badge color, headline, blurb). */
export const MASTERY_TIERS: Record<
  MasteryTier,
  {
    range: string;
    headline: string;
    blurb: string;
    /** Tailwind classes for the level pill — uses semantic tokens where possible. */
    pill: string;
    dot: string;
  }
> = {
  Awareness: {
    range: "1–20",
    headline: "Awareness",
    blurb: "Stop being a consumer zombie",
    pill: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    dot: "bg-emerald-400",
  },
  Application: {
    range: "21–40",
    headline: "Application",
    blurb: "Now you're useful",
    pill: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    dot: "bg-amber-400",
  },
  Leverage: {
    range: "41–60",
    headline: "Leverage",
    blurb: "You start multiplying yourself",
    pill: "border-orange-500/30 bg-orange-500/10 text-orange-400",
    dot: "bg-orange-400",
  },
  Systems: {
    range: "61–80",
    headline: "Systems",
    blurb: "Build machines that work without you",
    pill: "border-rose-500/30 bg-rose-500/10 text-rose-400",
    dot: "bg-rose-400",
  },
  Architecture: {
    range: "81–100",
    headline: "Architecture",
    blurb: "You build the future",
    pill: "border-primary/40 bg-primary/15 text-primary",
    dot: "bg-primary",
  },
};

const AWARENESS = [
  "What is AI (LLMs vs ML vs Automation)",
  "Difference between prompt vs program",
  "Using ChatGPT effectively",
  "Writing clear prompts (basic clarity)",
  "Asking better questions (context + constraints)",
  "Understanding hallucinations",
  "Using AI for summarization",
  "Using AI for rewriting",
  "Using AI for brainstorming",
  "Using AI for learning faster",
  "Prompt patterns (role, format, tone)",
  "Iterating prompts (not one-shot thinking)",
  "Comparing outputs across tools",
  "Using Claude for long-form reasoning",
  "Using Perplexity AI for research",
  "Basic AI ethics + bias awareness",
  "Copy/paste workflows",
  "Using AI daily (habit formation)",
  "Identifying where AI saves time in your life",
  "Replacing Google with AI thinking",
];

const APPLICATION = [
  "Writing emails with AI",
  "Creating content (posts, blogs)",
  "Resume + personal branding",
  "AI-assisted presentations",
  "Automating repetitive writing",
  "Using AI for sales scripts",
  "Objection handling with AI",
  "Customer research with AI",
  "Building simple workflows (copy → refine → publish)",
  "AI for idea validation",
  "Using AI for learning new skills fast",
  "Creating templates/prompts library",
  "AI for meeting summaries",
  "AI note-taking systems",
  "AI for decision-making frameworks",
  "Basic image generation with Midjourney",
  "AI video basics with Runway ML",
  "Repurposing content across channels",
  "Personal productivity systems with AI",
  "Building a “second brain” with AI",
];

const LEVERAGE = [
  "Prompt engineering (advanced structures)",
  "Chaining prompts together",
  "Creating multi-step workflows",
  "AI-assisted research pipelines",
  "Building content engines (not one-offs)",
  "SEO with AI systems",
  "AI for outbound personalization",
  "Lead generation systems with AI",
  "AI-assisted coding (non-technical)",
  "Using Zapier for automation",
  "Using Make (Integromat) for workflows",
  "Connecting tools together (APIs conceptually)",
  "AI for customer segmentation",
  "Building dashboards with AI insights",
  "Training AI on your tone/voice",
  "Creating internal AI playbooks",
  "AI for product ideation",
  "Rapid prototyping with AI",
  "Testing + iterating AI outputs",
  "Building repeatable systems",
];

const SYSTEMS = [
  "Understanding APIs (what they actually do)",
  "Calling APIs (basic)",
  "Working with OpenAI API",
  "Using embeddings + vector databases",
  "Building chatbots (real ones, not toys)",
  "Creating AI agents",
  "Multi-agent systems",
  "Memory systems for AI",
  "Building internal AI tools",
  "Automating business processes end-to-end",
  "AI + CRM integration",
  "AI for customer support systems",
  "Workflow orchestration",
  "Data pipelines for AI",
  "Fine-tuning models (conceptual + practical)",
  "Building no-code AI apps",
  "Using low-code frameworks",
  "Debugging AI systems",
  "Evaluating AI performance",
  "Designing scalable workflows",
];

const ARCHITECTURE = [
  "Designing AI-first businesses",
  "Building AI products",
  "Creating proprietary datasets",
  "Designing AI user experiences",
  "Human + AI interaction models",
  "AI ethics at scale",
  "Security + privacy in AI systems",
  "AI monetization strategies",
  "Building AI agencies",
  "Scaling AI infrastructure",
  "AI + organizational transformation",
  "Replacing departments with AI systems",
  "Designing AI ecosystems",
  "Multi-product AI platforms",
  "AI-driven marketplaces",
  "Network effects with AI",
  "Autonomous AI businesses",
  "AI + human augmentation frameworks",
  "Creating new categories with AI",
  "Defining the future of intelligence systems",
];

function build(tier: MasteryTier, labels: string[], startNum: number): MasterySkill[] {
  return labels.map((label, i) => ({ num: startNum + i, tier, label }));
}

/** All 100 skills in canonical order. */
export const AI_MASTERY_PATH: MasterySkill[] = [
  ...build("Awareness", AWARENESS, 1),
  ...build("Application", APPLICATION, 21),
  ...build("Leverage", LEVERAGE, 41),
  ...build("Systems", SYSTEMS, 61),
  ...build("Architecture", ARCHITECTURE, 81),
];

/**
 * A second ordering of the same 100 skills designed for the home-page
 * rotator: instead of marching tier-by-tier (which "lumps" 20 green
 * skills, then 20 amber, etc.), we interleave across all five tiers so
 * each visible trio mixes Awareness → Architecture. This lets visitors
 * sample the full range of the path within a few rotations and makes the
 * strip feel less repetitive.
 *
 * The order is deterministic (no Math.random at module load), so SSR /
 * snapshot tests stay stable. We zip the five tier arrays together,
 * round-robin, and then offset each "row" by 1 so adjacent trios don't
 * always show the same five tiers in the same slot positions.
 */
export const AI_MASTERY_PATH_SHUFFLED: MasterySkill[] = (() => {
  const tiers: MasterySkill[][] = [
    AI_MASTERY_PATH.filter((s) => s.tier === "Awareness"),
    AI_MASTERY_PATH.filter((s) => s.tier === "Application"),
    AI_MASTERY_PATH.filter((s) => s.tier === "Leverage"),
    AI_MASTERY_PATH.filter((s) => s.tier === "Systems"),
    AI_MASTERY_PATH.filter((s) => s.tier === "Architecture"),
  ];
  const out: MasterySkill[] = [];
  const maxLen = Math.max(...tiers.map((t) => t.length));
  for (let i = 0; i < maxLen; i++) {
    // Rotate the tier order each row so the same column doesn't always
    // show the same tier — keeps the visible trio varied as the strip
    // scrolls in groups of 3.
    const rotation = i % tiers.length;
    for (let j = 0; j < tiers.length; j++) {
      const tierIdx = (j + rotation) % tiers.length;
      const skill = tiers[tierIdx][i];
      if (skill) out.push(skill);
    }
  }
  return out;
})();

/**
 * AI App Builder skills — appended to the rotator path so the home-page
 * "launch" strip can surface concrete builder lessons (Lovable, Replit,
 * Base44, Primio, Bolt, v0). These are intentionally **NOT** part of the
 * canonical 100-step `AI_MASTERY_PATH` so we don't break the "100 skills"
 * promise — they live alongside it as a complementary track.
 *
 * Numbers start at 101 so badge rendering stays consistent (always shows
 * a `#N`), while making it visually obvious in the rotator that this is
 * an extension lane. Tier is `Application` because choosing and using a
 * builder is firmly an application-level skill (not architecture).
 *
 * Label conventions matter: PlaybookSearchHero.pickPlaybook does a
 * keyword substring match against playbook titles. Each label below
 * contains the builder's name (e.g. "Lovable"), and the matching
 * playbook in src/data/playbooks.ts also contains that name in its
 * title — so a click reliably routes to the right runner.
 */
export const AI_BUILDER_SKILLS: MasterySkill[] = [
  { num: 101, tier: "Application", label: "Ship an MVP with Lovable" },
  { num: 102, tier: "Application", label: "Scaffold a project with Replit Agent" },
  { num: 103, tier: "Application", label: "Build an internal tool with Base44" },
  { num: 104, tier: "Application", label: "Ship a landing page with Primio" },
  { num: 105, tier: "Application", label: "Prototype an app with Bolt.new" },
  { num: 106, tier: "Application", label: "Generate a UI with v0 by Vercel" },
];